import React, { memo } from 'react';
import { css, Theme, useTheme } from "@emotion/react";

import { ReactComponent as Chevron } from 'images/icons/rounded-chevron.svg';
import { NOOP } from "core/constants";
import { CheckoutStep } from "pages/Components/CartAndCheckout/CheckoutPage";

type Props = {
    steps: CheckoutStep[],
    currentStep: number,
    onNext: () => void,
    onBack: () => void,
    onStepClick: (step: number) => void,
}

const CheckoutPagination = ({ steps, currentStep, onNext = NOOP, onBack, onStepClick } : Props) => {
    const theme: Theme = useTheme();
    const isPreviousStepHidden = currentStep === 0 ? false : steps[currentStep - 1].isHidden;
    const goBackToCart = isPreviousStepHidden ? steps[currentStep - 1] === steps[0] : steps[currentStep] === steps[0];

    const chevronContainerStyle = (isDisabled = false) => css`
        font-size: 14px;
        
        ${isDisabled ? css`
            pointer-events: none;
            visibility: hidden;
        ` : css`
            color: ${theme.colours.curiousBlue2};
            cursor: pointer;
            transition: color ${theme.transitions.transitionFaster};

            > svg {
                 fill: ${theme.colours.curiousBlue2};
             }

            &:hover {
                color: ${theme.colours.gold2};
                svg {
                    fill: ${theme.colours.gold2};
                }
            }

            svg {
                transition: fill ${theme.transitions.transitionFaster};
            }
        `}
    `;

    const backChevronStyle = css`
        width: 18px;
        transform: rotate(-90deg);
    `;

    const nextChevronStyle = css`
        width: 18px;
        transform: rotate(90deg);
    `;

    const circleStyle = ({ isCurrent = false, isCompleted = false }) => css`
        width: 23px;
        height: 23px;
        background: ${isCompleted ? theme.colours.curiousBlue2 : 'white'};
        border-radius: 50%;
        border: 3px solid ${theme.colours[isCurrent || isCompleted ? 'curiousBlue2' : 'silver5']};
        scale: ${isCurrent ? 1.25 : 1};
    `;

    const lineStyle = ({ hasCompleted = false}) => css`
        width: 129px;
        height: 3px;
        background: ${theme.colours[hasCompleted ? 'curiousBlue2' : 'silver5']};
        
        ${theme.breakpoints.down('xs')}  {
            width: 55px;
        }
    `;

    const indicatorsStyle = css`
        display: flex;
        
        ${theme.breakpoints.down('xs')} {
            padding-left: 15px;
        }
    `;

    const indicatorContainerStyle = ({ isActive = false, isNext = false }) => css`
        display: flex;
        flex-direction: column;
        
        ${isActive ? css`
            cursor: pointer;
        ` : css`
            pointer-events: none;
        `};

        ${isNext && css`
            :hover {
                .label-step {
                    color: ${theme.colours.emperor};
                }

                .circle-step {
                    border: 3px solid ${theme.colours.emperor};
                }
            }
        `};
    `;

    const indicatorStepStyle = css`
        display: flex;
        align-items: center;
    `;

    const containerStyle = css`
        padding-top: 53px;
        display: flex;
        justify-content: space-between;
        
        ${theme.breakpoints.only('xl')} {
            padding-top: 22px;
        }

        ${theme.breakpoints.only('lg')} {
            padding-top: 26px;
        }

        ${theme.breakpoints.only('md')} {
            padding-top: 30px;
        }

        ${theme.breakpoints.only('sm')} {
            padding-top: 21px;
        }

        ${theme.breakpoints.down('xs')} {
            padding-top: 42px;
        }
    `;

    const stepLabelStyle = ({ hasHighlight = false }) => css`
        position: relative;
        transform: translateX(-50%);
        left: 11.5px;
        text-align: center;
        color: ${theme.colours[hasHighlight ? 'curiousBlue2' : 'gray3']};
        
        ${theme.breakpoints.down('xs')} {
            max-width: 55px;
        };
    `;

    return (
        <div css={containerStyle}>
            <div
                css={chevronContainerStyle()}
                onClick={onBack}
            >
                <Chevron css={backChevronStyle} />
                {goBackToCart ? 'Cart' : 'Back'}
            </div>
            <div>
                <div css={indicatorsStyle}>
                    {steps.map((step, index) => {
                        if (step.isHidden) return null;

                        const isCurrent = currentStep === index;
                        const isNext = currentStep + 1 === index;
                        const isPrevious = currentStep - 1 === index;
                        return (
                            <div
                                key={index}
                                css={indicatorContainerStyle({ isNext: isNext, isActive: onNext !== NOOP && (isNext || isPrevious) })}
                                onClick={isNext ? onNext : () => onStepClick(index)}
                            >
                                <div css={indicatorStepStyle}>
                                    <div
                                        css={circleStyle({ isCurrent, isCompleted: index < currentStep })}
                                        className={'circle-step'}
                                    />
                                    {index < steps.length - 1 && (
                                        <div css={lineStyle({ hasCompleted: index < currentStep })} />
                                    )}
                                </div>
                                <div
                                    css={stepLabelStyle({ hasHighlight: index < currentStep || isCurrent })}
                                    className={'label-step'}
                                >
                                    {step.label}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
            <div
                css={chevronContainerStyle(currentStep === steps.length - 1 || onNext === NOOP)}
                onClick={onNext}
            >
                Next
                <Chevron css={nextChevronStyle} />
            </div>
        </div>
    )
};

export default memo(CheckoutPagination);